<script>
    import { createEventDispatcher, onMount, getContext } from 'svelte';
    import { Language } from "../utils/store.js";
    import translations from "../utils/translations.js";
    const dispatch = createEventDispatcher();
    import 'animate.css';

    // Build translation map and helper
    const translationMap = {};
    translations.forEach(item => {
        if (item.eng && item.fr) translationMap[item.eng] = item.fr;
    });
    $: t = (text) => ($Language === 'fr' && translationMap[text]) ? translationMap[text] : text;

    export let question;
    export let policy;
    export let title;
    export let required = false;
    export let remove_options = [];
    export let increment = null;
    export let row_label = 'Entry';
    export let row_start = 1;

    const CONTEXT = getContext('CONTEXT');
    let savingQuestions = CONTEXT?.savingQuestions;

    let answer = policy.answers?.[question.id]?.text_answer || '';
    let debounceTimer;

    // Address autocomplete
    let addressInput;

    // Track selected tile choices for multi-select
    let selectedTileIds = [];

    // Custom "other" text for tiles
    let customOtherText = '';

    // Initialize custom other text from answer if subtitle is ALLOW OTHER
    $: if (question.type === "Tiles" && question.subtitle === 'ALLOW OTHER') {
        const currentAnswer = policy.answers?.[question.id];
        // If there's a text answer and no choice selected, it's custom text
        if (currentAnswer?.text_answer && !currentAnswer?.choice) {
            customOtherText = currentAnswer.text_answer;
        }
    }

    // Initialize selected tiles from answer
    $: if (question.type === "Tiles" && question.allow_multiple_answers) {
        const raw = policy.answers?.[question.id]?.other || '';
        selectedTileIds = raw.split(',').map(s => s.trim()).filter(Boolean);
    }

    // Collection state
    let collectionData = null;

    function blankInner(iq) {
        return { question: iq.uuid, text_answer: '', choice: null, other: '' };
    }

    function initCollection() {
        // Try to load from policy
        try {
            const saved = policy.answers?.[question.id]?.text_answer;
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.collections === 'number' && parsed.answers) {
                    collectionData = parsed;
                }
            }
        } catch (e) {}

        if (!collectionData) {
            collectionData = { collections: 1, answers: {} };
        }
        ensureCollectionShape();
    }

    function ensureCollectionShape() {
        const n = Math.max(1, parseInt(collectionData.collections) || 1);
        collectionData.collections = n;

        (question.inner_questions || []).forEach(iq => {
            if (!collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid] = [];
            for (let i = 0; i < n; i++) {
                if (!collectionData.answers[iq.uuid][i]) {
                    collectionData.answers[iq.uuid][i] = blankInner(iq);
                } else {
                    const a = collectionData.answers[iq.uuid][i];
                    if (a.question == null) a.question = iq.uuid;
                    if (a.text_answer == null) a.text_answer = '';
                    if (a.choice == null) a.choice = null;
                    if (a.other == null) a.other = '';
                }
            }
            if (collectionData.answers[iq.uuid].length > n) {
                collectionData.answers[iq.uuid].length = n;
            }
        });
    }

    function addCollectionRow() {
        collectionData.collections += 1;
        const idx = collectionData.collections - 1;
        (question.inner_questions || []).forEach(iq => {
            if (!collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid] = [];
            collectionData.answers[iq.uuid][idx] = blankInner(iq);
        });
        scheduleCollectionSave();
    }

    function removeCollectionRow(index) {
        if (collectionData.collections <= 1) return;
        (question.inner_questions || []).forEach(iq => {
            if (collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid].splice(index, 1);
        });
        collectionData.collections -= 1;
        scheduleCollectionSave();
    }

    function setInnerText(uuid, index, value) {
        const arr = collectionData.answers[uuid] || (collectionData.answers[uuid] = []);
        const obj = arr[index] || (arr[index] = { question: uuid, text_answer: '', choice: null, other: '' });
        obj.text_answer = value;
    }

    function onInnerInput(uuid, index, e) {
        setInnerText(uuid, index, e.target.value);
        scheduleCollectionSaveDebounced();
    }

    function onInnerInteger(uuid, index, e) {
        const v = e.target.value.replace(/\D/g, '');
        e.target.value = v;
        setInnerText(uuid, index, v);
        scheduleCollectionSaveDebounced();
    }

    function onInnerDate(uuid, index, e) {
        // Expect YYYY-MM-DD
        setInnerText(uuid, index, e.target.value);
        scheduleCollectionSaveDebounced();
    }

    function onInnerCurrency(uuid, index, e) {
        const rawValue = e.target.value;
        const digits = rawValue.replace(/\D/g, '');
        const formatted = digits ? parseInt(digits, 10).toLocaleString('en-US') : '';
        e.target.value = formatted;
        setInnerText(uuid, index, digits);
        scheduleCollectionSaveDebounced();
    }

    function onInnerAddressSelect(uuid, index, formatted) {
        setInnerText(uuid, index, formatted);
        scheduleCollectionSave();
    }

    function innerAddressAutocomplete(node, { uuid, index }) {
        let autocomplete = null;

        async function init() {
            // Check if Canada Post is enabled - use that instead of Google
            if (policy?.enable_canada_post_api) {
                try {
                    const apiKey = await fetchCanadaPostApiKey(policy.uuid);
                    if (!apiKey) return;

                    await loadCanadaPostScript(apiKey);

                    const fields = [
                        { element: node, field: '', mode: pca.fieldMode.SEARCH },
                    ];

                    const options = {
                        key: apiKey,
                        country: 'CA',
                        bar: { visible: false }
                    };

                    const control = new pca.Address(fields, options);

                    control.listen('populate', function(address) {
                        const formatted = address.Label.replaceAll('\n', ', ');
                        node.value = formatted;
                        onInnerAddressSelect(uuid, index, formatted);
                    });
                } catch (e) {
                    console.error('Canada Post failed to load for inner address:', e);
                }
            } else {
                // Use Google Places
                try {
                    await ensureGooglePlacesLoaded();

                    if (!window.google?.maps?.places) {
                        console.error('Google Places not available');
                        return;
                    }

                    const options = {
                        componentRestrictions: { country: policy.countries || [] }
                    };
                    autocomplete = new window.google.maps.places.Autocomplete(node, options);

                    autocomplete.addListener('place_changed', () => {
                        const place = autocomplete.getPlace();
                        const formatted = place?.formatted_address || '';
                        node.value = formatted;
                        onInnerAddressSelect(uuid, index, formatted);
                    });
                } catch (e) {
                    console.error('Google Places failed to load for inner address:', e);
                }
            }
        }

        init();

        return {
            destroy() {
                autocomplete = null;
            }
        };
    }

    function getInnerText(uuid, index) {
        return (collectionData?.answers?.[uuid]?.[index]?.text_answer) || '';
    }

    function scheduleCollectionSave() {
        const payload = { collections: collectionData.collections, answers: collectionData.answers };
        dispatch('answered', {
            questionId: question.id,
            text_answer: JSON.stringify(payload)
        });
    }

    function scheduleCollectionSaveDebounced() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            scheduleCollectionSave();
        }, 400);
    }

    // Initialize once, and refresh if server pushes a newer value
    onMount(async () => {
        if (question.type === "Collection") {
            initCollection();
        }
    });

    $: if (question.type === "Collection") {
        const server = policy.answers?.[question.id]?.text_answer || '';
        if (server && (!collectionData || collectionData._server !== server)) {
            try {
                collectionData = JSON.parse(server);
                collectionData._server = server;
                ensureCollectionShape();
            } catch (e) {}
        }
    }

    function loadScriptOnce(id, src) {
        return new Promise((resolve, reject) => {
            if (document.getElementById(id)) return resolve();
            const s = document.createElement('script');
            s.id = id;
            s.src = src;
            s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error(`Failed to load ${src}`));
            document.head.appendChild(s);
        });
    }

    async function ensureGooglePlacesLoaded() {
        if (window.google && window.google.maps && window.google.maps.places) return;
        if (!window.google_maps_api_key) {
            throw new Error('Missing window.google_maps_api_key');
        }
        const src = `https://maps.googleapis.com/maps/api/js?key=${window.google_maps_api_key}&libraries=places`;
        await loadScriptOnce('gmaps-places', src);
    }

    function setupAddressAutocomplete() {
        if (!addressInput) return;
        const options = {
            componentRestrictions: { country: policy.countries || [] }
        };
        const autocomplete = new window.google.maps.places.Autocomplete(addressInput, options);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const formatted = place?.formatted_address || '';
            addressInput.value = formatted;
            answer = formatted;

            dispatch('answered', {
                questionId: question.id,
                text_answer: formatted
            });
        });
    }

    function handleAnswer(e) {
        answer = e.target.value;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (question.type === "Text") {
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: answer
                });
            }
        }, 500);
    }

    function handleBooleanAnswer(value) {
        answer = value;
        dispatch('answered', {
            questionId: question.id,
            text_answer: value
        });
    }

    async function handleCalendarAnswer(e) {
        answer = e.target.value;
    
        // Dispatch the answer first
        dispatch('answered', {
            questionId: question.id,
            text_answer: answer
        });
        
        // If this is an effective date question, wait a bit for the answer to save
        // then dispatch the date update
        if (question.effective_date) {
            // Wait for the batched save to complete (400ms debounce + request time)
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Calculate end date: add 365 days to the start date
            const startDate = new Date(answer);
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 365);
            
            // Format dates as YYYY-MM-DD
            const formattedEndDate = endDate.toISOString().split('T')[0];
            
            dispatch('updatedEffectiveDate', {
                start_date: answer,
                end_date: formattedEndDate
            });
        }
    }

    function handleTileAnswer(choice) {
        // Clear custom other text when selecting a tile
        customOtherText = '';
        
        if (question.allow_multiple_answers) {
            // Multi-select: toggle the choice
            const choiceIdStr = String(choice.id);
            const index = selectedTileIds.indexOf(choiceIdStr);
            
            if (index > -1) {
                // Deselect
                selectedTileIds = selectedTileIds.filter(id => id !== choiceIdStr);
            } else {
                // Select
                selectedTileIds = [...selectedTileIds, choiceIdStr];
            }

            // Build labels from selected IDs
            const labelById = {};
            question.questionchoice_set.forEach(c => {
                labelById[String(c.id)] = c.answer;
            });
            const labels = selectedTileIds.map(id => labelById[id]).filter(Boolean);

            dispatch('answered', {
                questionId: question.id,
                text_answer: labels.join(', '),
                choice: selectedTileIds.length ? selectedTileIds[0] : null,
                choices: selectedTileIds,
                other: selectedTileIds.join(',')
            });
        } else {
            // Single select
            const isCurrentlySelected = policy.answers?.[question.id]?.choice === choice.id;
            const isRequired = question.required || required;
            
            // Allow deselection if not required and already selected
            if (isCurrentlySelected && !isRequired) {
                answer = '';
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: '',
                    choice: null
                });
            } else {
                answer = choice.answer;
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: choice.answer,
                    choice: choice.id
                });
            }
        }
    }

    function handleIntegerAnswer(e) {
        // Only allow digits
        const value = e.target.value.replace(/\D/g, '');
        e.target.value = value;
        answer = value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: value
            });
        }, 500);
    }

    function handleCustomOtherText(e) {
        customOtherText = e.target.value;
        
        // Clear tile selections
        selectedTileIds = [];
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (customOtherText.trim()) {
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: customOtherText.trim(),
                    choice: null,
                    choices: [],
                    other: ''
                });
            }
        }, 500);
    }

    // Currency formatting and handler
    function formatCurrency(value) {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        if (!digits) return '';
        
        // Convert to number and format with commas
        const number = parseInt(digits, 10);
        return number.toLocaleString('en-US');
    }

    function handleCurrencyAnswer(e) {
        const rawValue = e.target.value;
        const formatted = formatCurrency(rawValue);
        e.target.value = formatted;
        
        // Store the raw number without formatting
        const rawNumber = rawValue.replace(/\D/g, '');
        answer = rawNumber;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: rawNumber
            });
        }, 500);
    }

    // Check if a tile choice is selected
    function isTileSelected(choiceId) {
        if (question.allow_multiple_answers) {
            return selectedTileIds.includes(String(choiceId));
        }
        return policy.answers?.[question.id]?.choice === choiceId;
    }

    function formatDate(dateString) {
        if (!dateString) return '';
        // Parse date string as local date to avoid timezone issues
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        const locale = $Language === 'fr' ? 'fr-CA' : 'en-US';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    onMount(async () => {
        if (question.type === "Address") {
            try {
                await ensureGooglePlacesLoaded();
                setupAddressAutocomplete();
            } catch (e) {
                console.error('Google Places failed to load:', e);
            }
        }
    });

    // Phone number formatting
    function formatPhoneNumber(value) {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX for North American numbers
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else if (digits.length <= 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else {
            // Handle longer numbers (international)
            return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
        }
    }

    // Email validation
    function isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // Phone number handler
    function handlePhoneAnswer(e) {
        const rawValue = e.target.value;
        const formatted = formatPhoneNumber(rawValue);
        e.target.value = formatted;
        answer = formatted;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: formatted
            });
        }, 500);
    }

    // Email handler with validation
    function handleEmailAnswer(e) {
        answer = e.target.value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: answer
            });
        }, 500);
    }

    // Year validation and handler
    function isValidYear(year) {
        const yearNum = parseInt(year);
        return yearNum >= 1900 && yearNum <= 2026;
    }

    function handleYearAnswer(e) {
        // Only allow digits and limit to 4 characters
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 4) {
            value = value.slice(0, 4);
        }
        e.target.value = value;
        answer = value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: value
            });
        }, 500);
    }


    // File Upload handling (CORRECTED - saves URL from response)
    let uploadProgress = 0;
    let uploadStatus = '';
    let isUploading = false;
    let uploadedFiles = []; // Array to store multiple file URLs

    // Initialize uploadedFiles from existing answer
    $: if (question.type === "FileUpload" && answer) {
        try {
            // Try to parse as JSON array
            uploadedFiles = JSON.parse(answer);
            if (!Array.isArray(uploadedFiles)) {
                // If it's a single URL string, convert to array
                uploadedFiles = answer ? [answer] : [];
            }
        } catch (e) {
            // If parsing fails, treat as single URL
            uploadedFiles = answer ? [answer] : [];
        }
    }

    async function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        isUploading = true;
        uploadProgress = 0;
        uploadStatus = 'Uploading...';

        try {
            const policyUuid = policy.uuid;
            
            const form = new FormData();
            form.append('question-' + question.id, file);

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    uploadProgress = Math.round((e.loaded / e.total) * 100);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        uploadStatus = 'Upload Complete';
                        
                        if (response.url) {
                            // Add new file to the array
                            uploadedFiles = [...uploadedFiles, response.url];
                            
                            // Store as JSON array
                            answer = JSON.stringify(uploadedFiles);
                            
                            // Dispatch with the JSON array as the answer
                            dispatch('answered', {
                                questionId: question.id,
                                text_answer: answer
                            });
                        }

                        setTimeout(() => {
                            isUploading = false;
                            // Reset file input to allow same file again
                            e.target.value = '';
                        }, 1000);
                    } catch (error) {
                        console.error('Error parsing upload response:', error);
                        uploadStatus = 'Upload failed';
                        isUploading = false;
                    }
                } else {
                    console.error('Upload failed with status:', xhr.status);
                    uploadStatus = 'Upload failed';
                    isUploading = false;
                }
            });

            xhr.addEventListener('error', () => {
                uploadStatus = 'Upload failed';
                isUploading = false;
            });

            xhr.open('POST', `/api/policy/policy/${policyUuid}/files/`);
            xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            
            const token = localStorage.getItem("insurecert_token");
            if (token) {
                xhr.setRequestHeader('Authorization', `JWT ${token}`);
            }
            
            xhr.send(form);

        } catch (error) {
            console.error('File upload error:', error);
            uploadStatus = 'Upload failed';
            isUploading = false;
        }
    }

    function deleteFile(index) {
        // Remove file at index
        uploadedFiles = uploadedFiles.filter((_, i) => i !== index);
        
        // Update answer
        answer = uploadedFiles.length > 0 ? JSON.stringify(uploadedFiles) : '';
        
        // Dispatch update
        dispatch('answered', {
            questionId: question.id,
            text_answer: answer
        });
    }

    function getFileName(filePath) {
        if (!filePath) return '';
        // Handle full URLs (extract filename from path)
        if (filePath.startsWith('http')) {
            const urlPath = new URL(filePath).pathname;
            return urlPath.split('/').pop();
        }
        // Handle local paths
        return filePath.split('/').pop().split('\\').pop();
    }

    function handleParagraphAnswer(e) {
        answer = e.target.value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: answer
            });
        }, 500);
    }

    function handlePercentageAnswer(e) {
        let value = e.target.value;
        
        // Allow digits and one decimal point
        value = value.replace(/[^\d.]/g, '');
        
        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Limit to 2 decimal places
        if (parts[1] && parts[1].length > 2) {
            value = parts[0] + '.' + parts[1].slice(0, 2);
        }
        
        // Limit to 100
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 100) {
            value = '100';
        }
        
        e.target.value = value;
        answer = value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: value
            });
        }, 500);
    }

    // Per-row conditional inner questions, keyed by inner question text
    // (same convention as the flat Office Package questions on the page).
    function innerValueByText(allInnerQuestions, idx, text) {
        const iq = (allInnerQuestions || []).find(q => (q.question_text || '').trim().toLowerCase() === text);
        return iq ? (getInnerText(iq.uuid, idx) || '').trim() : '';
    }

    function conditionalInnerQuestionVisible(iq, idx, allInnerQuestions) {
        const text = (iq.question_text || '').trim().toLowerCase();
        const yes = (t) => innerValueByText(allInnerQuestions, idx, t).toLowerCase() === 'yes';
        if (['roof', 'plumbing', 'heating', 'electrical'].includes(text)) {
            return yes('is the building more than 25 years old?');
        }
        if (text === 'mortgagee/loss payee name' || text === 'mortgagee/loss payee address') {
            return yes('do you require a mortgagee/loss payee added to this location?');
        }
        const owns = ['Own Building', 'Own Condo'].includes(innerValueByText(allInnerQuestions, idx, 'do you lease or own this premises?'));
        if (text === 'do you wish to insure this building?') {
            return owns;
        }
        if (text === 'building limit') {
            return owns && yes('do you wish to insure this building?');
        }
        return true;
    }

    function shouldShowInnerQuestion(iq, idx, allInnerQuestions) {
        if (!conditionalInnerQuestionVisible(iq, idx, allInnerQuestions)) {
            return false;
        }
        // Check if this is a "specify other" type question
        const isSpecifyOther = iq.question_text && iq.question_text.toLowerCase().includes('specify other');
        
        if (!isSpecifyOther) {
            // Always show questions that aren't "specify other"
            return true;
        }
        
        // For "specify other" questions, check if the previous question has "Other (specify)" selected
        const sortedQuestions = allInnerQuestions.sort((a, b) => a.position - b.position);
        const currentIndex = sortedQuestions.findIndex(q => q.uuid === iq.uuid);
        
        if (currentIndex > 0) {
            const previousQuestion = sortedQuestions[currentIndex - 1];
            const previousValue = getInnerText(previousQuestion.uuid, idx);
            
            // Show if previous value is "Other (specify)" or similar variations
            return previousValue && (
                previousValue.toLowerCase().includes('other (specify)') ||
                previousValue.toLowerCase().includes('other(specify)') ||
                previousValue.toLowerCase() === 'other'
            );
        }
        
        return false;
    }

    function getRevenuePercentageSum() {
        if (!collectionData || !question.inner_questions) return 0;
        
        // Find the "Percent of total revenue" inner question
        const revenuePercentQuestion = question.inner_questions.find(
            iq => iq.question_text && iq.question_text.toLowerCase().includes('percent of total revenue')
        );
        
        if (!revenuePercentQuestion) return null;
        
        // Sum all values for this question across all collection rows
        let sum = 0;
        const answers = collectionData.answers[revenuePercentQuestion.uuid] || [];
        
        for (let i = 0; i < collectionData.collections; i++) {
            const value = answers[i]?.text_answer || '0';
            const numValue = parseFloat(value) || 0;
            sum += numValue;
        }
        
        return { sum, questionUuid: revenuePercentQuestion.uuid };
    }

    // Reactive variable to track the sum
    $: revenuePercentageData = question.type === "Collection" ? getRevenuePercentageSum() : null;


    function handleSliderInput(e) {
        const value = e.target.value;
        answer = value;
        // Update slider fill dynamically
        updateSliderFill(e.target);
    }

    function handleSliderChange(e) {
        const value = e.target.value;
        answer = value;
        dispatch('answered', {
            questionId: question.id,
            text_answer: value
        });
    }

    function handleSliderOptOut() {
        if (answer === question.subtitle) {
            // Unselect - reset to first choice
            answer = '';
        } else {
            // Select opt-out
            answer = question.subtitle;
            dispatch('answered', {
                questionId: question.id,
                text_answer: question.subtitle
            });
        }
    }

    function updateSliderFill(slider) {
        if (!slider) return;
        const min = parseFloat(slider.min);
        const max = parseFloat(slider.max);
        const value = parseFloat(slider.value);
        const percentage = ((value - min) / (max - min)) * 100;
        slider.style.setProperty('--slider-percentage', `${percentage}%`);
    }

    // Initialize slider fill on mount
    onMount(() => {
        if (question.type === "CurrencySlider") {
            const slider = document.querySelector('.currency-slider');
            if (slider && answer && (!question.subtitle || answer !== question.subtitle)) {
                updateSliderFill(slider);
            }
        }
    });

    // Canada Post API key fetching
    async function fetchCanadaPostApiKey(policyUuid) {
        if (window.canada_post_api_key) {
            return window.canada_post_api_key;
        }

        try {
            const response = await fetch(`/api/policy/policy/${policyUuid}/post_address_key/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')?.value || ''
                }
            });
            const data = await response.json();
            if (data.api_key) {
                window.canada_post_api_key = data.api_key;
                return data.api_key;
            }
        } catch (e) {
            console.error('Failed to fetch Canada Post API key:', e);
        }
        return null;
    }

    // Load Canada Post script dynamically
    function loadCanadaPostScript(apiKey) {
        return new Promise((resolve, reject) => {
            if (typeof pca !== 'undefined') {
                resolve();
                return;
            }

            if (document.getElementById('canada-post-script')) {
                // Script already loading, wait for it
                const checkPca = setInterval(() => {
                    if (typeof pca !== 'undefined') {
                        clearInterval(checkPca);
                        resolve();
                    }
                }, 100);
                return;
            }

            // Load CSS
            const css = document.createElement('link');
            css.rel = 'stylesheet';
            css.href = 'https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css';
            document.head.appendChild(css);

            // Load JS
            const script = document.createElement('script');
            script.id = 'canada-post-script';
            script.src = `https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js?key=${apiKey}`;
            script.onload = () => {
                console.log('Canada Post script loaded');
                resolve();
            };
            script.onerror = () => reject(new Error('Failed to load Canada Post script'));
            document.head.appendChild(script);
        });
    }

    // Canada Post autocomplete Svelte action
    function canadaPostAutocomplete(node, { onSelect, policy }) {
        let control = null;

        async function init() {
            console.log('canadaPostAutocomplete initializing...');

            // Only proceed if Canada Post is enabled
            if (!policy?.enable_canada_post_api) {
                console.log('Canada Post API not enabled, skipping');
                return;
            }

            // Fetch API key if not already available
            const apiKey = await fetchCanadaPostApiKey(policy.uuid);
            if (!apiKey) {
                console.warn('No Canada Post API key available');
                return;
            }

            console.log('Got API key, loading script...');

            // Load the Canada Post script
            try {
                await loadCanadaPostScript(apiKey);
            } catch (e) {
                console.error('Failed to load Canada Post script:', e);
                return;
            }

            console.log('Script loaded, initializing autocomplete...');

            // Initialize the autocomplete
            const fields = [
                { element: node, field: '', mode: pca.fieldMode.SEARCH },
            ];

            const options = {
                key: apiKey,
                country: 'CA',
                bar: { visible: false }
            };

            control = new pca.Address(fields, options);

            control.listen('populate', function(address) {
                const formatted = address.Label.replaceAll('\n', ', ');
                const addressData = {
                    full_address: formatted,
                    address: address.Line1 || '',
                    city: address.City || '',
                    state: address.ProvinceName || '',
                    postal_code: address.PostalCode || '',
                    country: address.CountryName || 'Canada'
                };
                onSelect(addressData);
            });

            console.log('Canada Post autocomplete initialized successfully');
        }

        // Start initialization
        init();

        return {
            destroy() {
                // Cleanup if needed
                control = null;
            }
        };
    }

    function getCanadaPostAddress(ans) {
        if (!ans?.text_answer) return null;
        try {
            return typeof ans.text_answer === 'string'
                ? JSON.parse(ans.text_answer)
                : ans.text_answer;
        } catch {
            return { full_address: ans.text_answer };
        }
    }

    // Get display value for the search input
    function getCanadaPostDisplayValue(ans) {
        const addr = getCanadaPostAddress(ans);
        return addr?.full_address || '';
    }
</script>

<div class="question-wrapper animate__animated animate__fadeInUp">
    <div class="question-header">
        <h3>
            {t(title || question.title || question.question)}
            {#if question.required || required}
                <span class="required-indicator" title="Required">*</span>
            {/if}
        </h3>
        {#if question.help_text}
            <div class="help-icon-wrapper">
                <svg class="help-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 16v.01M12 13a1 1 0 0 1 1-1 2 2 0 1 0-2-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div class="help-tooltip">
                    {t(question.help_text)}
                </div>
            </div>
        {/if}
    </div>
    
    {#if savingQuestions && $savingQuestions?.[question.id]}
        <div class="saving-indicator">Saving…</div>
    {/if}
    
    {#if question.type === "Text"}
        {#if question.question.toLowerCase().includes('phone') || question.question.toLowerCase().includes('telephone')}
            <!-- Phone Number Input -->
            <input 
                type="tel" 
                name="question-{question.id}" 
                value={answer} 
                on:input={handlePhoneAnswer}
                on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                placeholder={question.placeholder_text || '(555) 123-4567'}
                class="phone-input"
            />
        {:else if question.question.toLowerCase().includes('email')}
            <!-- Email Input -->
            <div class="email-wrapper">
                <input 
                    type="email" 
                    name="question-{question.id}" 
                    value={answer} 
                    on:input={handleEmailAnswer}
                    on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                    placeholder={question.placeholder_text || 'your@email.com'}
                    class="email-input"
                    class:invalid={answer && !isValidEmail(answer)}
                />
                {#if answer && !isValidEmail(answer)}
                    <div class="email-error">Please enter a valid email address</div>
                {/if}
            </div>
        {:else}
            <!-- Regular Text Input -->
            <input 
                type="text" 
                name="question-{question.id}" 
                value={answer} 
                on:input={handleAnswer}
                on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                placeholder={question.placeholder_text || ''}
            />
        {/if}
    {/if}

    {#if question.type === "CanadaPostAddress"}
        <div class="canada-post-address-wrapper">
            <div class="canada-post-search">
                <input
                    type="text"
                    class="canada-post-address-input"
                    placeholder={t("Start typing an address then select it")}
                    value={getCanadaPostDisplayValue(policy.answers?.[question.id])}
                    autocomplete="off"
                    use:canadaPostAutocomplete={{
                        policy: policy,
                        onSelect: (addressData) => {
                            answer = JSON.stringify(addressData);

                            // Dispatch to save
                            dispatch('answered', {
                                questionId: question.id,
                                text_answer: JSON.stringify(addressData)
                            });
                        }
                    }}
                    on:input={(e) => {
                        // Allow manual typing
                        answer = e.target.value;
                    }}
                />
            </div>
        </div>
    {/if}

    {#if question.type === "Boolean"}
        <div class="boolean-options">
            <button 
                class="boolean-button" 
                class:selected={answer === 'yes'}
                on:click={() => handleBooleanAnswer('yes')}
            >
                <div class="button-content">
                    <!-- <svg class="checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg> -->
                    <span>{t("Yes")}</span>
                </div>
            </button>

            <button
                class="boolean-button"
                class:selected={answer === 'no'}
                on:click={() => handleBooleanAnswer('no')}
            >
                <div class="button-content">
                    <!-- <svg class="checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg> -->
                    <span>{t("No")}</span>
                </div>
            </button>
        </div>
    {/if}

    {#if question.type === "Calendar"}
        <div class="calendar-wrapper">
            <div class="calendar-display" class:calendar-missing={!answer && (question.required || required)}>
                <svg class="calendar-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                {#if question.question.toLowerCase().includes('member since:')}
                    <input 
                        type="date" 
                        class="calendar-input"
                        name="question-{question.id}" 
                        value={answer} 
                        autocomplete="new-password"
                        max={new Date().toISOString().split('T')[0]}
                        on:change={handleCalendarAnswer}
                    />
                {:else}
                    <input
                        type="date"
                        class="calendar-input"
                        name="question-{question.id}"
                        value={answer}
                        autocomplete="new-password"
                        min={question.effective_date ? new Date().toISOString().split('T')[0] : undefined}
                        on:change={handleCalendarAnswer}
                    />
                {/if}
                <span class="calendar-display-text">
                    {answer ? formatDate(answer) : t('Select a date')}
                </span>
            </div>
        </div>
    {/if}

    {#if question.type === "Address"}
        <input
            type="text"
            class="address-input"
            name="question-{question.id}"
            placeholder={t("Start typing your address...")}
            bind:this={addressInput}
            autocomplete="new-password"
            value={answer}
        />
    {/if}

    {#if question.type === "Integer" || question.type === "Number"}
        {#if question.question.toLowerCase().includes('year established')}
            <div class="year-wrapper">
                <input 
                    type="text" 
                    inputmode="numeric"
                    pattern="[0-9]*"
                    name="question-{question.id}" 
                    value={answer} 
                    on:input={handleYearAnswer}
                    on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                    placeholder={question.placeholder_text || t('YYYY')}
                    class="year-input"
                    autocomplete="new-password"
                    class:invalid={answer && answer.length === 4 && !isValidYear(answer)}
                    maxlength="4"
                />
                {#if answer && answer.length === 4 && !isValidYear(answer)}
                    <div class="year-error">Please enter a year between 1900 and 2026</div>
                {/if}
            </div>
        {:else}
            <div class="number-wrapper">
                <input 
                    type="text" 
                    inputmode="numeric"
                    pattern="[0-9]*"
                    class="number-input"
                    name="question-{question.id}" 
                    value={answer} 
                    autocomplete="new-password"
                    on:input={handleIntegerAnswer}
                    on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                    placeholder={question.placeholder_text || t('Enter a number')}
                />
            </div>
        {/if}
    {/if}

    {#if question.type === "Tiles"}
        <div class="tiles-wrapper">
            {#each [...question.questionchoice_set].sort((a, b) => a.position - b.position).filter((choice, index) => !remove_options.includes(index)) as choice (choice.id)}
                <button 
                    class="tile-button" 
                    class:selected={isTileSelected(choice.id)}
                    on:click={() => handleTileAnswer(choice)}
                >
                    <div class="tile-content">
                        {#if choice.icon}
                            <div class="tile-icon">
                                {@html choice.icon}
                            </div>
                        {/if}
                        <span class="tile-text">{t(choice.answer)}</span>
                    </div>
                </button>
            {/each}
        </div>
        
        {#if question.subtitle === 'ALLOW OTHER'}
            <div class="custom-other-wrapper">
                <div class="divider">
                    <span>{t('OR')}</span>
                </div>
                <input 
                    type="text" 
                    class="custom-other-input"
                    placeholder={t("Enter your own answer...")}
                    value={customOtherText}
                    on:input={handleCustomOtherText}
                />
            </div>
        {/if}
    {/if}
    {#if question.type === "Paragraph"}
        <div class="paragraph-wrapper">
            <textarea
                class="paragraph-input"
                name="question-{question.id}"
                value={answer}
                on:input={handleParagraphAnswer}
                placeholder={question.placeholder_text || t('Enter your response...')}
                rows="6"
            ></textarea>
            {#if answer}
                <div class="character-count">
                    {answer.length} characters
                </div>
            {/if}
        </div>
    {/if}

    {#if question.type === "Percentage"}
        <div class="percentage-wrapper">
            <div class="percentage-input-container">
                <input 
                    type="text" 
                    inputmode="decimal"
                    pattern="[0-9.]*"
                    class="percentage-input"
                    name="question-{question.id}" 
                    value={answer}
                    on:input={handlePercentageAnswer}
                    on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                    placeholder={question.placeholder_text || '0'}
                    maxlength="6"
                />
                <span class="percentage-symbol">%</span>
            </div>
            {#if answer && (parseFloat(answer) < 0 || parseFloat(answer) > 100)}
                <div class="percentage-error">Please enter a value between 0 and 100</div>
            {/if}
        </div>
    {/if}

    {#if question.type === "Collection"}
        <div class="collection-wrapper">
            {#if collectionData}
                {#each Array(collectionData.collections) as _, idx}
                    <div class="collection-row">
                        <div class="collection-row-header">
                            <div class="collection-row-title">{t(row_label)} {idx + row_start}</div>
                            {#if collectionData.collections > 1}
                                <button class="remove-row" on:click={() => removeCollectionRow(idx)}>Remove</button>
                            {/if}
                        </div>
                        <div class="collection-row-body">
                            {#each (question.inner_questions || []).sort((a, b) => a.position - b.position) as iq (iq.uuid)}
                                {#if shouldShowInnerQuestion(iq, idx, question.inner_questions)}
                                    <div class="inner-question">
                                        <label>{t(iq.question_text)}</label>
                                        {#if iq.type === 'Text' && (iq.questionchoice_set?.length > 0 || iq.choices?.length > 0)}
                                            <select value={getInnerText(iq.uuid, idx)} on:change={(e) => onInnerInput(iq.uuid, idx, e)}>
                                                <option value="">{t('Please select...')}</option>
                                                {#each (iq.questionchoice_set || iq.choices || []).sort((a, b) => a.position - b.position) as choice (choice.id)}
                                                    <option value={choice.answer}>{t(choice.answer)}</option>
                                                {/each}
                                            </select>
                                        {:else if iq.type === 'Text'}
                                            <input type="text" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInput(iq.uuid, idx, e)} />
                                        {:else if iq.type === 'Integer' || iq.type === 'Number'}
                                            <input type="text" inputmode="numeric" pattern="[0-9]*" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInteger(iq.uuid, idx, e)} />
                                        {:else if iq.type === 'Currency'}
                                            <div class="inner-currency-input-container">
                                                <span class="currency-symbol">$</span>
                                                <input type="text" inputmode="numeric" pattern="[0-9,]*" class="currency-input" value={formatCurrency(getInnerText(iq.uuid, idx))} on:input={(e) => onInnerCurrency(iq.uuid, idx, e)} placeholder={iq.placeholder_text || '0'} />
                                            </div>
                                        {:else if iq.type === 'Address'}
                                            <input
                                                type="text"
                                                class="inner-address-input"
                                                value={getInnerText(iq.uuid, idx)}
                                                on:input={(e) => onInnerInput(iq.uuid, idx, e)}
                                                placeholder={iq.placeholder_text || t('Start typing an address...')}
                                                autocomplete="new-password"
                                                use:innerAddressAutocomplete={{ uuid: iq.uuid, index: idx }}
                                            />
                                        {:else if iq.type && iq.type.startsWith('Calendar')}
                                            <input type="date" value={getInnerText(iq.uuid, idx)} on:change={(e) => onInnerDate(iq.uuid, idx, e)} />
                                        {:else}
                                            <input type="text" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInput(iq.uuid, idx, e)} />
                                        {/if}
                                    </div>
                                {/if}
                            {/each}
                        </div>
                    </div>
                {/each}
                {#if revenuePercentageData}
                    <div class="revenue-percentage-validation" class:valid={Math.abs(revenuePercentageData.sum - 100) < 0.01} class:invalid={Math.abs(revenuePercentageData.sum - 100) >= 0.01}>
                        <div class="validation-main">
                            <span class="validation-label">{($Language === 'fr') ? "Pourcentage de recettes annuelles brutes" : 'Total Revenue Percentage'}</span>
                            <span class="validation-value">{revenuePercentageData.sum.toFixed(1)}%</span>
                        </div>
                        {#if Math.abs(revenuePercentageData.sum - 100) < 0.01}
                            <div class="validation-badge valid-badge">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                {($Language === 'fr') ? "Valide" : 'Valid'}
                            </div>
                        {:else}
                            <div class="validation-badge invalid-badge">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 4.66667V7M7 9.33333H7.00667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5"/>
                                </svg>
                                {($Language === 'fr') ? "Doit être égal à 100%" : 'Must equal 100%'}
                            </div>
                        {/if}
                    </div>
                {/if}
                <div class="collection-actions">
                    <button class="add-row" on:click={addCollectionRow}>{question.placeholder_text || t('+ Add another')}</button>
                </div>
            {/if}
        </div>
    {/if}
    {#if question.type === "Currency"}
        <div class="currency-wrapper">
            <div class="currency-input-container">
                <span class="currency-symbol">$</span>
                <input 
                    type="text" 
                    inputmode="numeric"
                    pattern="[0-9,]*"
                    class="currency-input"
                    name="question-{question.id}" 
                    value={formatCurrency(answer)}
                    on:input={handleCurrencyAnswer}
                    on:blur={() => dispatch('answered', { questionId: question.id, text_answer: answer })}
                    placeholder={question.placeholder_text || ''}
                />
            </div>
        </div>
    {/if}
    {#if question.type === "FileUpload"}
        <div class="upload-wrapper">
            <input
                type="file"
                id="file-{question.id}"
                class="upload-input"
                name="question-{question.id}"
                on:change={handleFileUpload}
                accept={question.placeholder_text || '*'}
            />
            <label for="file-{question.id}" class="upload-label">
                <div class="upload-icon-wrapper">
                    <svg class="upload-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <polyline points="17 8 12 3 7 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="upload-text">
                    {#if uploadedFiles.length > 0}
                        <strong class="upload-filename">
                            {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded
                        </strong>
                        <span class="upload-change">Click to add another file</span>
                    {:else}
                        <strong>Choose a file</strong>
                        <span class="upload-hint">or drag and drop</span>
                    {/if}
                </div>
            </label>

            {#if isUploading}
                <div class="upload-loading">
                    <div class="spinner"></div>
                    <div class="loading-status">{uploadStatus}</div>
                </div>
            {/if}

            {#if uploadedFiles.length > 0 && !isUploading}
                <div class="uploaded-files-list">
                    <div class="uploaded-files-header">Uploaded Files:</div>
                    {#each uploadedFiles as fileUrl, index (fileUrl)}
                        <div class="uploaded-file-item">
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" class="file-link">
                                <svg class="file-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <polyline points="14 2 14 8 20 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span class="file-name">{getFileName(fileUrl)}</span>
                            </a>
                            <button 
                                type="button"
                                class="delete-file-button" 
                                on:click={() => deleteFile(index)}
                                title="Delete this file"
                            >
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    {/each}
                </div>
            {/if}

        </div>
    {/if}

    {#if question.type === "CurrencySlider"}
        {@const filteredChoices = [...question.questionchoice_set].sort((a, b) => a.position - b.position).filter((choice, index) => !remove_options.includes(index))}
        {@const sliderMin = filteredChoices.length > 0 ? parseInt(filteredChoices[0].answer.replace(/[$,]/g, '')) : 0}
        {@const sliderMax = filteredChoices.length > 0 ? parseInt(filteredChoices[filteredChoices.length - 1].answer.replace(/[$,]/g, '')) : 0}
        {@const answerNum = parseInt(answer)}
        {@const outOfRange = answer && (!question.subtitle || answer !== question.subtitle) && !isNaN(answerNum) && (answerNum < sliderMin || answerNum > sliderMax)}
        <div class="currency-slider-wrapper">
            <div class="slider-container" class:disabled={question.subtitle && answer === question.subtitle}>
                <div class="slider-value-display" class:no-answer={!answer || (question.subtitle && answer === question.subtitle)} class:out-of-range={outOfRange}>
                    {#if answer && (!question.subtitle || answer !== question.subtitle)}
                        ${parseInt(answer || 0).toLocaleString('en-US')}
                    {:else}
                        $--
                    {/if}
                </div>

                <input
                    type="range"
                    class="currency-slider"
                    min={sliderMin}
                    max={sliderMax}
                    step={increment || question.increment || 25000}
                    value={(question.subtitle && answer === question.subtitle) ? '' : (answer || filteredChoices[0]?.answer.replace(/[$,]/g, ''))}
                    on:input={handleSliderInput}
                    on:change={handleSliderChange}
                    disabled={question.subtitle && answer === question.subtitle}
                />

                <div class="slider-labels">
                    <span class="slider-label-min">
                        ${sliderMin.toLocaleString('en-US')}
                    </span>
                    <span class="slider-label-max">
                        ${sliderMax.toLocaleString('en-US')}
                    </span>
                </div>
                {#if outOfRange}
                    <div class="slider-error">Value must be between ${sliderMin.toLocaleString('en-US')} and ${sliderMax.toLocaleString('en-US')}</div>
                {/if}
            </div>
            {#if question.subtitle}
                <div class="slider-divider">
                    <span>OR SELECT AMOUNT</span>
                </div>
                <div class="slider-opt-out">
                    <button 
                        class="opt-out-button"
                        class:selected={answer === question.subtitle}
                        on:click={() => handleSliderOptOut()}
                    >
                        {question.subtitle}
                    </button>
                </div>
                
            {/if}
        </div>
    {/if}
</div>

<style>
    .question-wrapper {
        text-align: center;
        padding: 2rem 1.5rem;
        padding-bottom: 0;
        max-width: 500px;
        margin: 0 auto;
    }
    .question-wrapper {
        animation-duration: 0.6s !important;
        animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    /* Question Header with Help Icon */
    .question-header {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
    }

    h3 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.4;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .required-indicator {
        color: var(--red);
        font-size: 1.375rem;
        font-weight: 700;
        margin-left: 0.25rem;
        vertical-align: super;
        line-height: 0;
        display: inline-block;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: scale(0.8);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    .help-icon-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        cursor: help;
    }

    .paragraph-wrapper {
        width: 100%;
    }

    .paragraph-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        line-height: 1.6;
        resize: vertical;
        min-height: 120px;
    }

    .paragraph-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .paragraph-input::placeholder {
        color: #86868b;
    }

    .character-count {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: #86868b;
        text-align: right;
        font-weight: 500;
    }

    @media (max-width: 480px) {
        .paragraph-input {
            padding: 0.75rem 0.875rem;
            font-size: 1rem;
            min-height: 100px;
        }
    }


    .help-icon {
        width: 20px;
        height: 20px;
        color: #86868b;
        transition: color 0.2s ease;
    }

    .help-icon-wrapper:hover .help-icon {
        color: var(--base-green);
    }

    .help-tooltip {
        position: absolute;
        top: calc(100% + 0.5rem);
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        line-height: 1.4;
        white-space: normal;
        min-width: 200px;
        max-width: 300px;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 1000;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .help-tooltip::before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-bottom-color: rgba(0, 0, 0, 0.9);
    }

    .help-icon-wrapper:hover .help-tooltip {
        opacity: 1;
    }

    @media (max-width: 480px) {
        .help-tooltip {
            left: auto;
            right: 0;
            transform: none;
            min-width: 250px;
        }
        
        .help-tooltip::before {
            left: auto;
            right: 1rem;
            transform: none;
        }
    }

    input[type="text"] {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    input[type="text"]:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    input[type="text"]::placeholder {
        color: #86868b;
    }

    /* Override Chrome autocomplete background color */
    input[type="text"]:-webkit-autofill,
    input[type="text"]:-webkit-autofill:hover,
    input[type="text"]:-webkit-autofill:focus,
    input[type="text"]:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
        -webkit-text-fill-color: #1d1d1f !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Green tint for autocomplete hover/focus */
    input[type="text"]:-webkit-autofill:hover {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.05) inset !important;
        border-color: var(--base-green) !important;
    }

    input[type="text"]:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.08) inset !important;
        border-color: var(--base-green) !important;
    }

    .boolean-options {
        display: flex;
        gap: 1rem;
        width: 100%;
    }

    .boolean-button {
        flex: 1;
        padding: 1.25rem 1.5rem;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        position: relative;
        overflow: hidden;
    }

    .boolean-button:hover {
        border-color:  var(--base-green);
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }

    .boolean-button:active {
        transform: translateY(0);
    }

    .boolean-button.selected {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-color: var(--base-green);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }

    .button-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
    }

    .checkmark {
        width: 32px;
        height: 32px;
        color: #d2d2d7;
        transition: all 0.25s ease;
    }

    .boolean-button.selected .checkmark {
        color: #fff;
        animation: checkScale 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .boolean-button:not(.selected) .checkmark {
        opacity: 0.3;
    }

    .button-content span {
        font-size: 1.125rem;
        font-weight: 500;
        color: #1d1d1f;
        transition: color 0.25s ease;
        letter-spacing: -0.01em;
    }

    .boolean-button.selected .button-content span {
        color: #fff;
    }

    /* Calendar Styles */
    .calendar-wrapper {
        width: 100%;
    }

    .calendar-display {
        position: relative;
        width: 100%;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        box-sizing: border-box;
        padding: 1.5rem 1.25rem;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .calendar-display:hover {
        border-color: var(--base-green);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.12);
        transform: translateY(-2px);
    }

    .calendar-display:has(.calendar-input:focus) {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .calendar-display.calendar-missing {
        border-color: #dc3545;
    }

    .calendar-icon {
        width: 28px;
        height: 28px;
        color: var(--base-green);
        flex-shrink: 0;
    }

    .calendar-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        z-index: 2;
    }

    .calendar-input::-webkit-calendar-picker-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        opacity: 0;
    }

    .calendar-display-text {
        font-size: 1.0625rem;
        font-weight: 500;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        flex: 1;
        text-align: left;
    }

    .calendar-display:not(:has(input[value])) .calendar-display-text,
    .calendar-display:has(input[value=""]) .calendar-display-text {
        color: #86868b;
    }

    @keyframes checkScale {
        0% {
            transform: scale(0.8);
            opacity: 0;
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    @media (max-width: 480px) {
        .boolean-options {
            flex-direction: column;
        }

        .boolean-button {
            width: 100%;
        }
    }

    .address-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    .address-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    /* Override Chrome autocomplete for address inputs */
    .address-input:-webkit-autofill,
    .address-input:-webkit-autofill:hover,
    .address-input:-webkit-autofill:focus,
    .address-input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
        -webkit-text-fill-color: #1d1d1f !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    .address-input:-webkit-autofill:hover {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.05) inset !important;
        border-color: var(--base-green) !important;
    }

    .address-input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.08) inset !important;
        border-color: var(--base-green) !important;
    }

    /* Tiles Styles */
    .tiles-wrapper {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 1rem;
        width: 100%;
        margin-top: 0.5rem;
    }

    .tile-button {
        position: relative;
        padding: 1.5rem 1rem;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .tile-button:hover {
        border-color: var(--base-green);
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(49, 104, 50, 0.15);
    }

    .tile-button:active {
        transform: translateY(-2px);
    }

    .tile-button.selected {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-color: var(--base-green);
        box-shadow: 0 12px 32px rgba(49, 104, 50, 0.3);
    }

    .tile-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        position: relative;
    }

    .tile-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.25s ease;
    }

    .tile-button.selected .tile-icon {
        transform: scale(1.1);
    }

    .tile-text {
        font-size: 1rem;
        font-weight: 500;
        color: #1d1d1f;
        transition: color 0.25s ease;
        letter-spacing: -0.01em;
        text-align: center;
        line-height: 1.4;
    }

    .tile-button.selected .tile-text {
        color: #fff;
        font-weight: 600;
    }


    @media (max-width: 480px) {
        .tiles-wrapper {
            grid-template-columns: 1fr;
            gap: 0.75rem;
        }

        .tile-button {
            min-height: 100px;
            padding: 1.25rem 1rem;
        }
    }

    @media (min-width: 481px) and (max-width: 768px) {
        .tiles-wrapper {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    /* Number Input Styles */
    .number-wrapper {
        width: 100%;
    }

    .number-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: center;
        letter-spacing: -0.02em;
    }

    .number-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .number-input::placeholder {
        color: #86868b;
        font-weight: 400;
        font-size: 1.0625rem;
    }

    /* Remove spinner buttons on number inputs */
    .number-input::-webkit-outer-spin-button,
    .number-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .number-input[type=number] {
        -moz-appearance: textfield;
    }

    /* Custom Other Input for Tiles */
    .custom-other-wrapper {
        margin-top: 1.5rem;
        width: 100%;
    }

    .divider {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 1rem 0;
        color: #86868b;
        font-size: 0.875rem;
        font-weight: 500;
    }

    .divider::before,
    .divider::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #d2d2d7;
    }

    .divider span {
        padding: 0 1rem;
    }

    .custom-other-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #f5f5f7;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .custom-other-input:focus {
        outline: none;
        border-color: #0071e3;
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
    }

    .custom-other-input::placeholder {
        color: #86868b;
        font-style: italic;
    }

    .saving-indicator {
        margin: -0.5rem 0 0.75rem;
        color: #86868b;
        font-size: 0.875rem;
        font-weight: 500;
    }

    /* Collection Styles */
    .collection-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin-top: 1rem;
    }

    .collection-row {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        padding: 1.5rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .collection-row:hover {
        box-shadow: 0 8px 32px rgba(0, 113, 227, 0.08);
        border-color: #d2d2d7;
    }

    .collection-row-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.25rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #e9ecef;
    }

    .collection-row-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .remove-row {
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        padding: 0.5rem 1rem;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--red);
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .remove-row:hover {
        border-color: var(--red);
        background: var(--red);
        color: #fff;
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(219, 93, 93, 0.15);
    }

    .remove-row:active {
        transform: translateY(0);
    }

    .add-row {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 2rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 600;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        letter-spacing: -0.01em;
        box-shadow: 0 4px 16px rgba(49, 104, 50, 0.2);
    }

    .add-row:hover {
        background: linear-gradient(135deg, var(--base-green-light) 0%, var(--base-green-lightest) 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 28px rgba(49, 104, 50, 0.3);
    }

    .add-row:active {
        transform: translateY(0);
    }

    .collection-row-body {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .inner-question {
        display: flex;
        flex-direction: column;
    }

    .inner-question label {
        display: block;
        text-align: left;
        font-size: 1rem;
        margin-bottom: 0.5rem;
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .inner-question input[type="text"],
    .inner-question input[type="date"] {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .inner-question input[type="text"]:focus,
    .inner-question input[type="date"]:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .inner-question input[type="text"]::placeholder {
        color: #86868b;
    }

    .collection-actions {
        display: flex;
        justify-content: center;
        margin-top: 0.5rem;
    }

    @media (max-width: 480px) {
        .collection-row {
            padding: 1.25rem;
            border-radius: 16px;
        }

        .collection-row-title {
            font-size: 1rem;
        }

        .remove-row {
            padding: 0.4rem 0.75rem;
            font-size: 0.8125rem;
        }

        .add-row {
            padding: 0.75rem 1.5rem;
            font-size: 0.9375rem;
        }

        .inner-question label {
            font-size: 0.9375rem;
        }

        .inner-question input[type="text"],
        .inner-question input[type="date"] {
            padding: 0.75rem 0.875rem;
            font-size: 1rem;
        }
    }

    @media (min-width: 481px) and (max-width: 768px) {
        .collection-row-body {
            gap: 0.875rem;
        }
    }

    /* Phone Input Styles */
    .phone-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        letter-spacing: 0.02em;
    }

    .phone-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    /* Email Input Styles */
    .email-wrapper {
        width: 100%;
    }

    .email-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .email-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .email-input.invalid {
        border-color: var(--red);
        box-shadow: 0 0 0 4px rgba(172, 57, 49, 0.1);
    }

    .email-error {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: var(--red);
        font-weight: 500;
        text-align: left;
    }

    /* Year Input Styles */
    .year-wrapper {
        width: 100%;
    }

    .year-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: center;
        letter-spacing: 0.1em;
    }

    .year-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .year-input.invalid {
        border-color: var(--red);
        box-shadow: 0 0 0 4px rgba(172, 57, 49, 0.1);
    }

    .year-input::placeholder {
        color: #86868b;
        font-weight: 400;
        /* letter-spacing: 0.05em; */
    }

    .year-error {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: var(--red);
        font-weight: 500;
        text-align: center;
    }

     /* Currency Input Styles */
     .currency-wrapper {
        width: 100%;
    }

    .currency-input-container {
        position: relative;
        display: flex;
        align-items: center;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        transition: all 0.2s ease;
        overflow: hidden;
    }

    .currency-input-container:focus-within {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .currency-symbol {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.875rem 0 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 600;
        color: var(--base-green);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        user-select: none;
    }

    .currency-input {
        flex: 1;
        padding: 0.875rem 1rem 0.875rem 0.5rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: transparent;
        border: none !important;
        box-shadow: none !important;
        box-sizing: border-box;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: left;
        letter-spacing: -0.02em;
    }

    .currency-input:focus {
        outline: none;
    }

    .currency-input::placeholder {
        color: #86868b;
        font-weight: 400;
        font-size: 1.0625rem;
    }

    /* Remove spinner buttons on currency inputs */
    .currency-input::-webkit-outer-spin-button,
    .currency-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .currency-input[type=number] {
        -moz-appearance: textfield;
    }

    @media (max-width: 480px) {
        .currency-symbol {
            padding: 0.75rem 0 0.75rem 0.875rem;
            font-size: 1.25rem;
        }
        
        .currency-input {
            padding: 0.75rem 0.875rem 0.75rem 0.5rem;
            font-size: 1.25rem;
        }
    }

    /* File Upload Styles */
    .upload-wrapper {
        width: 100%;
    }

    .upload-input {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;
    }

    .upload-label {
        display: flex;
        align-items: center;
        gap: 1.5rem;
        padding: 2rem 1.5rem;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px dashed #d2d2d7;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
    }

    .upload-label:hover {
        border-color: var(--base-green);
        background: linear-gradient(135deg, rgba(49, 104, 50, 0.05) 0%, #ffffff 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.1);
    }

    .upload-input:focus + .upload-label {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .upload-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 4px 16px rgba(49, 104, 50, 0.2);
    }

    .upload-icon {
        width: 32px;
        height: 32px;
        color: #fff;
    }

    .upload-text {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        flex: 1;
    }

    .upload-text strong {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .upload-text span {
        font-size: 0.9375rem;
        color: #86868b;
        font-weight: 400;
    }

    .upload-filename {
        color: var(--base-green);
        word-break: break-word;
    }

    .upload-change {
        font-size: 0.875rem !important;
        font-style: italic;
    }

    .upload-hint {
        font-size: 0.875rem;
    }

    /* Progress Bar */
    .progress-bar-wrapper {
        margin-top: 1rem;
        width: 100%;
    }

    .progress-bar {
        width: 100%;
        height: 12px;
        background: #e9ecef;
        border-radius: 6px;
        overflow: hidden;
        position: relative;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-radius: 6px;
        transition: width 0.3s ease;
        box-shadow: 0 2px 8px rgba(49, 104, 50, 0.3);
    }

    .progress-status {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: #86868b;
        font-weight: 500;
        text-align: center;
    }

    /* Upload Success */
    .upload-success {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-top: 1rem;
        padding: 1rem;
        background: rgba(49, 104, 50, 0.1);
        border: 1px solid var(--base-green);
        border-radius: 12px;
        color: var(--base-green);
        font-size: 0.9375rem;
        font-weight: 500;
    }

    .success-icon {
        width: 24px;
        height: 24px;
        color: var(--base-green);
        flex-shrink: 0;
    }

    /* Skip Button */
    .skip-button {
        margin-top: 1rem;
        padding: 0.75rem 1.5rem;
        background: transparent;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        color: #86868b;
        font-size: 0.9375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        width: 100%;
    }

    .skip-button:hover {
        border-color: var(--base-green);
        color: var(--base-green);
        background: rgba(49, 104, 50, 0.05);
    }

    .skip-button:active {
        transform: scale(0.98);
    }

    @media (max-width: 480px) {
        .upload-label {
            flex-direction: column;
            gap: 1rem;
            padding: 1.5rem 1rem;
            text-align: center;
        }

        .upload-icon-wrapper {
            width: 56px;
            height: 56px;
        }

        .upload-icon {
            width: 28px;
            height: 28px;
        }

        .upload-text strong {
            font-size: 1rem;
        }

        .upload-text span {
            font-size: 0.875rem;
        }
    }

    /* Uploaded Files List */
    .uploaded-files-list {
        margin-top: 1rem;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 12px;
        padding: 1rem;
    }

    .uploaded-files-header {
        font-size: 0.875rem;
        font-weight: 600;
        color: #1d1d1f;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.025em;
    }

    .uploaded-file-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem;
        background: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 8px;
        margin-bottom: 0.5rem;
        transition: all 0.2s ease;
    }

    .uploaded-file-item:last-child {
        margin-bottom: 0;
    }

    .uploaded-file-item:hover {
        border-color: var(--base-green);
        box-shadow: 0 2px 8px rgba(49, 104, 50, 0.1);
    }

    .file-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
        text-decoration: none;
        color: var(--base-green);
        font-weight: 500;
        font-size: 0.9375rem;
        transition: color 0.2s ease;
    }

    .file-link:hover {
        color: var(--base-green-light);
    }

    .file-icon {
        width: 20px;
        height: 20px;
        color: var(--base-green);
        flex-shrink: 0;
    }

    .file-name {
        word-break: break-word;
    }

    .delete-file-button {
        background: transparent;
        border: 1px solid #d2d2d7;
        border-radius: 6px;
        padding: 0.375rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        flex-shrink: 0;
    }

    .delete-file-button svg {
        width: 18px;
        height: 18px;
        color: #86868b;
    }

    .delete-file-button:hover {
        background: var(--red);
        border-color: var(--red);
    }

    .delete-file-button:hover svg {
        color: #fff;
    }

    .delete-file-button:active {
        transform: scale(0.95);
    }

    @media (max-width: 480px) {
        .uploaded-file-item {
            padding: 0.625rem;
        }
        
        .file-link {
            font-size: 0.875rem;
        }
        
        .file-icon {
            width: 18px;
            height: 18px;
        }
        
        .delete-file-button {
            padding: 0.3rem;
        }
        
        .delete-file-button svg {
            width: 16px;
            height: 16px;
        }
    }

    .inner-question select {
        width: 100%;
        padding: 0.75rem;
        font-size: 1rem;
        border: 1.5px solid #d2d2d7;
        border-radius: 8px;
        background: white;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        transition: border-color 0.2s ease;
    }

    .inner-question select:focus {
        outline: none;
        border-color: var(--base-green);
    }

    /* Inner Currency Input Styles */
    .inner-currency-input-container {
        position: relative;
        display: flex;
        align-items: center;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        transition: all 0.2s ease;
        overflow: hidden;
    }

    .inner-currency-input-container:focus-within {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .inner-currency-input-container .currency-symbol {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.75rem 0 0.75rem 0.875rem;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--base-green);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        user-select: none;
    }

    .inner-currency-input-container .currency-input {
        flex: 1;
        padding: 0.75rem 0.875rem 0.75rem 0.5rem;
        font-size: 1rem;
        font-weight: 500;
        color: #1d1d1f;
        border: none;
        background: transparent;
        outline: none;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .inner-currency-input-container .currency-input::placeholder {
        color: #86868b;
        font-weight: 400;
    }

    /* Inner Address Input Styles */
    .inner-address-input {
        width: 100%;
        padding: 0.75rem 0.875rem;
        font-size: 1rem;
        font-weight: 500;
        color: #1d1d1f;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        box-sizing: border-box;
    }

    .inner-address-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .inner-address-input::placeholder {
        color: #86868b;
        font-weight: 400;
    }

    /* Percentage Input Styles */
.percentage-wrapper {
    width: 100%;
}

.percentage-input-container {
    position: relative;
    display: flex;
    align-items: center;
    background: #fff;
    border: 2px solid #d2d2d7;
    border-radius: 12px;
    transition: all 0.2s ease;
    overflow: hidden;
}

.percentage-input-container:focus-within {
    border-color: var(--base-green);
    box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
}

.percentage-input {
    flex: 1;
    padding: 0.875rem 0.5rem 0.875rem 1rem;
    font-size: 1.375rem;
    font-weight: 500;
    color: #1d1d1f;
    background-color: transparent;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    text-align: right;
    letter-spacing: -0.02em;
}

.percentage-input:focus {
    outline: none;
}

.percentage-input::placeholder {
    color: #86868b;
    font-weight: 400;
    font-size: 1.0625rem;
}

.percentage-symbol {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.875rem 1rem 0.875rem 0;
    font-size: 1.375rem;
    font-weight: 600;
    color: var(--base-green);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    user-select: none;
}

.percentage-error {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: var(--red);
    font-weight: 500;
    text-align: center;
}

/* Remove spinner buttons on percentage inputs */
.percentage-input::-webkit-outer-spin-button,
.percentage-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.percentage-input[type=number] {
    -moz-appearance: textfield;
}

@media (max-width: 480px) {
    .percentage-input {
        padding: 0.75rem 0.5rem 0.75rem 0.875rem;
        font-size: 1.25rem;
    }
    
    .percentage-symbol {
        padding: 0.75rem 0.875rem 0.75rem 0;
        font-size: 1.25rem;
    }
}
/* Revenue Percentage Validation */
.revenue-percentage-validation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-radius: 14px;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid;
    backdrop-filter: blur(10px);
}

.revenue-percentage-validation.valid {
    background: linear-gradient(135deg, rgba(49, 104, 50, 0.08) 0%, rgba(49, 104, 50, 0.04) 100%);
    border-color: rgba(49, 104, 50, 0.3);
}

.revenue-percentage-validation.invalid {
    background: linear-gradient(135deg, rgba(172, 57, 49, 0.08) 0%, rgba(172, 57, 49, 0.04) 100%);
    border-color: rgba(172, 57, 49, 0.3);
}

.validation-main {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.validation-label {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1d1d1f;
    letter-spacing: -0.01em;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.validation-value {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.revenue-percentage-validation.valid .validation-value {
    color: var(--base-green);
}

.revenue-percentage-validation.invalid .validation-value {
    color: var(--red);
}

.validation-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    transition: all 0.2s ease;
    letter-spacing: -0.01em;
}

.valid-badge {
    background: var(--base-green);
    color: white;
    box-shadow: 0 2px 8px rgba(49, 104, 50, 0.3);
}

.invalid-badge {
    background: white;
    color: var(--red);
    border: 1.5px solid var(--red);
}

.validation-badge svg {
    flex-shrink: 0;
}

@media (max-width: 480px) {
    .revenue-percentage-validation {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
    }
    
    .validation-main {
        width: 100%;
        justify-content: space-between;
    }
    
    .validation-label {
        font-size: 0.875rem;
    }
    
    .validation-value {
        font-size: 1.375rem;
    }
    
    .validation-badge {
        align-self: stretch;
        justify-content: center;
    }
}

/* Currency Slider Styles */
.currency-slider-wrapper {
    width: 100%;
}

.slider-opt-out {
    margin-bottom: 1rem;
}

.opt-out-button {
    width: 100%;
    padding: 1rem 1.5rem;
    background: #fff;
    border: 2px solid #d2d2d7;
    border-radius: 12px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    color: #1d1d1f;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.opt-out-button:hover {
    border-color: var(--base-green);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(49, 104, 50, 0.15);
}

.opt-out-button.selected {
    background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
    border-color: var(--base-green);
    color: white;
    box-shadow: 0 4px 12px rgba(49, 104, 50, 0.25);
}

.slider-divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 1.5rem 0;
    color: #86868b;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.05em;
}

.slider-divider::before,
.slider-divider::after {
    content: '';
    flex: 1;
    border-bottom: 2px solid #e9ecef;
}

.slider-divider span {
    padding: 0 1rem;
}

.slider-container {
    width: 100%;
    padding: 2rem 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px solid #e9ecef;
    border-radius: 16px;
    transition: all 0.3s ease;
    box-sizing: border-box;
}

.slider-container.disabled {
    opacity: 0.4;
    pointer-events: none;
}

.slider-value-display {
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    color: var(--base-green);
    margin-bottom: 2rem;
    letter-spacing: -0.02em;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    min-height: 2.5rem;
}

.slider-value-display.no-answer,
.slider-value-display.out-of-range {
    color: #e53e3e;
}

.slider-error {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #e53e3e;
    font-weight: 500;
    text-align: center;
}

.currency-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
        to right,
        var(--base-green) 0%,
        var(--base-green) var(--slider-percentage, 0%),
        #e9ecef var(--slider-percentage, 0%),
        #e9ecef 100%
    );
    outline: none;
    cursor: pointer;
    transition: all 0.2s ease;
    --slider-percentage: 0%;
}

.currency-slider:hover {
    opacity: 0.9;
}

.currency-slider:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

/* Webkit browsers (Chrome, Safari, Edge) */
.currency-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    border: 3px solid var(--base-green);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(49, 104, 50, 0.3);
    transition: all 0.2s ease;
}

.currency-slider::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(49, 104, 50, 0.4);
}

.currency-slider::-webkit-slider-thumb:active {
    transform: scale(1.05);
}

/* Firefox */
.currency-slider::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    border: 3px solid var(--base-green);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(49, 104, 50, 0.3);
    transition: all 0.2s ease;
}

.currency-slider::-moz-range-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(49, 104, 50, 0.4);
}

.currency-slider::-moz-range-thumb:active {
    transform: scale(1.05);
}

.slider-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    padding: 0 0.25rem;
}

.slider-label-min,
.slider-label-max {
    font-size: 0.875rem;
    font-weight: 600;
    color: #86868b;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

@media (max-width: 480px) {
    .slider-container {
        padding: 1.5rem 1rem;
    }
    
    .slider-value-display {
        font-size: 1.75rem;
        margin-bottom: 1.5rem;
    }
    
    .opt-out-button {
        padding: 0.875rem 1.25rem;
        font-size: 0.9375rem;
    }
}

/* Canada Post Address Styles */
.canada-post-address-wrapper {
    width: 100%;
}

.canada-post-search {
    margin-bottom: 12px;
}

.canada-post-address-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
}

.canada-post-address-input:focus {
    outline: none;
    border-color: var(--primary-color, #4a90d9);
    box-shadow: 0 0 0 2px rgba(74, 144, 217, 0.1);
}

.canada-post-fields {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.address-field label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.address-field input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
}

.address-field-row {
    display: flex;
    gap: 12px;
}

.address-field-row .address-field {
    flex: 1;
}
</style>